import FieldWrapper from '@features/ui-form/components/FieldWrapper';
import Autocomplete from '@features/ui-form/components/Autocomplete';
import { WrappedSelectProps } from '@features/ui-form/types';
import type { ChangeEvent } from 'react';
import { TFunction, useTranslation } from 'next-i18next';
import { Orbit, ORBITS } from './enum';

const OrbitAutocomplete = Autocomplete(false, false);

const orbitOptions = (t: TFunction) =>
  Object.values(ORBITS).map((orbit: Orbit) => ({
    value: orbit,
    label: t(`spaceObject.orbit.${orbit}`),
    labelString: t(`spaceObject.orbit.${orbit}`),
  }));

export interface OrbitInputProps {
  /** An orbit, `''` for none. */
  value: string;
  onChange: (orbit: string) => void;
  error?: boolean;
  placeholder?: string;
}

/**
 * Picks one orbit among the enum. Holds the plain value: `disableSerialization`
 * makes the Autocomplete speak it as is, `''` on clear. Wrap it in a
 * `FormField` (or a `FieldWrapper`) for its label and error.
 */
export const OrbitInput = ({ value, onChange, error, placeholder }: OrbitInputProps) => {
  const { t } = useTranslation('common');

  return (
    <OrbitAutocomplete
      value={value}
      onChange={event => onChange(event.target.value)}
      options={orbitOptions(t)}
      placeholder={placeholder ?? t('spaceObject.orbit.placeholder')}
      error={error}
      disableSerialization
    />
  );
};

export const OrbitsInput = ({
  value,
  onChange,
  label,
  placeholder,
  hideError = false,
  required = false,
  ...rest
}: WrappedSelectProps) => {
  const { t } = useTranslation('common');
  const OrbitsAutocomplete = Autocomplete(true, false);

  const handleChange = (event: { target: { value: string } }) => {
    if (!onChange) return;

    const raw = event.target.value;
    let parsed: string[] = [];

    if (raw) {
      try {
        const tmp = JSON.parse(raw) as string[];
        if (Array.isArray(tmp)) parsed = tmp;
      } catch {
        parsed = [];
      }
    }

    onChange({
      target: {
        name: (rest as { name?: string }).name ?? 'orbit',
        value: parsed,
      },
    } as unknown as ChangeEvent<HTMLSelectElement>);
  };

  return (
    <FieldWrapper name="orbit" label={label || t('spaceObject.orbit')} hideError={hideError} required={required}>
      {OrbitsAutocomplete({
        value: Array.isArray(value) ? JSON.stringify(value) : '[]',
        onChange: handleChange,
        options: orbitOptions(t),
        placeholder: placeholder || t('spaceObject.orbit.placeholder'),
      })}
    </FieldWrapper>
  );
};
